/* WISAMIN - product description formatter
   Converts the flat <p>/<strong> description into the .wp-ar article layout.
   Safe: if anything looks unexpected it leaves the original markup alone.

   Numbered sub-blocks
   -------------------
   A multi-flavour product writes its nutrition like this:

     (check)Nutrition per 50g (2 scoops) :
     1 Cocoa Chocolate
     - Protein : 30g
     ...
     2 Matcha Green Tea
     - Protein : 30g

   where the flavour headings are keycap emoji. Before this, only the check
   emoji and the dash were recognised, so every flavour name fell through as a
   paragraph and all eighty nutrition lines merged into ONE table. The shopper
   saw ten identical-looking blocks with no idea which flavour each belonged
   to - which is exactly what a nutrition panel must never be ambiguous about.

   So a keycap / circled / "1." heading now opens a GROUP inside the current
   section, and every line after it belongs to that group until the next one.

   Groups render as a card grid, every card open. The first version of this
   collapsed them behind an accordion, and that was the wrong call: it saved
   about 330px and cost the shopper the one thing the panel is for, which is
   reading the sugar and protein of one flavour against another. Nutrition is
   what a supplement buyer decides on. It does not go behind a click.

   The card that matches the flavour selected in #wpVariant is marked, so the
   grid stays comparable AND still answers "which one is mine".

   The grouping only engages when it clearly is a group list - at least two
   groups, and at least half of them carrying two or more lines. Anything else
   folds straight back into the old flat shape, so a stray "1)" in a paragraph
   cannot change how an existing description renders.

   Reserving space for the figures
   -------------------------------
   This used to strip width and height off every image. Nothing replaced them,
   so each figure was a zero-height box until its file arrived and then snapped
   open to 520px, shoving the text below it down the page. Someone reading the
   description got the page yanked out from under their thumb, image after
   image.

   Putting the attributes back does NOT fix it on its own - the stylesheet
   pins .wp-ar__fig img to width:auto !important / height:auto !important, and
   an auto width with no loaded file measures zero no matter what the
   attributes say. That is almost certainly why they were stripped in the first
   place: they were doing nothing.

   So the size is reserved the only way that beats an !important rule - an
   inline aspect-ratio plus an inline !important width, computed from the
   attributes and from whatever max-height the theme currently applies. Read
   at runtime, never hardcoded, so the article and product-page caps (430px
   and 520px today) both stay correct and a future change to either needs no
   edit here.

   An image that carries no dimensions at all cannot be measured before it
   arrives. Those get a square placeholder, then their real attributes the
   instant the file lands - a shift only if the image was not square, instead
   of a guaranteed shift from nothing to full height. */
(function () {
  'use strict';

  var ICONS = {
    spec:  'M4 5h16M4 12h16M4 19h10',
    box:   'M3 7l9-4 9 4v10l-9 4-9-4z M3 7l9 4 9-4 M12 11v10',
    cert:  'M12 3l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.4 6.8 19.1l1-5.8L3.5 9.2l5.9-.9z',
    shield:'M12 3l7 3v6c0 4.2-2.9 7.6-7 9-4.1-1.4-7-4.8-7-9V6z M9 12l2 2 4-4',
    leaf:  'M5 19c0-8 5-13 14-13 0 9-5 14-13 14 0-4 2-7 6-9',
    flask: 'M9 3h6 M10 3v6l-5 9a2 2 0 002 3h10a2 2 0 002-3l-5-9V3 M7.5 15h9',
    heart: 'M12 20s-7-4.3-7-9a4 4 0 017-2.6A4 4 0 0119 11c0 4.7-7 9-7 9z',
    cup:   'M6 4h10v6a5 5 0 01-10 0z M16 6h2a2 2 0 010 4h-2 M5 20h12',
    clock: 'M12 21a9 9 0 100-18 9 9 0 000 18z M12 7v5l3 2',
    warn:  'M12 4l9 16H3z M12 10v4 M12 17h.01',
    info:  'M12 21a9 9 0 100-18 9 9 0 000 18z M12 11v5 M12 8h.01',
    gut:   'M6 4c0 4 12 4 12 8s-12 4-12 8',
    star:  'M12 3l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.4 6.8 19.1l1-5.8L3.5 9.2l5.9-.9z'
  };

  var RULES = [
    [/warn|caution|not suitable|avoid|side effect|allerg/i, 'warn',  'warn'],
    [/store|storage|keep in|shelf life|expiry/i,            'clock', 'misc'],
    [/how to|directions|suggested use|recommended intake|serving suggestion|preparation/i, 'cup', 'how'],
    [/halal|certif|fda|gmp|sgs|iso|haccp|registration/i,    'cert',  'cert'],
    [/ingredient|composition|blend|contains|formula/i,      'flask', 'ing'],
    [/probiotic|prebiotic|gut|digest|fib(re|er)/i,          'gut',   'misc'],
    [/benefit|highlight|why choose|advantage|key feature/i, 'star',  'feat'],
    [/nutrition|protein|amino|vitamin|mineral|calorie/i,    'leaf',  'misc'],
    [/weight|quantity|size|serving|net|package|packing/i,   'box',   'spec'],
    [/overview|detail|specification|product info/i,         'spec',  'spec'],
    [/flavor|flavour|taste/i,                               'heart', 'misc']
  ];

  /* keycap emoji (1 .. 9), the ten emoji, circled digits, or a plain "1." / "2)" */
  var SUB_RE = /^(?:[0-9]️?⃣|🔟|[①-⑳]|\(?\d{1,2}[.)])\s*/;

  function pick(title) {
    for (var i = 0; i < RULES.length; i++) if (RULES[i][0].test(title)) return { icon: RULES[i][1], mod: RULES[i][2] };
    return { icon: 'info', mod: 'misc' };
  }

  function svg(key) {
    var d = ICONS[key] || ICONS.info;
    var paths = d.split(' M').map(function (p, i) { return '<path d="' + (i ? 'M' + p : p) + '"/>'; }).join('');
    return '<span class="wp-ar__hi"><svg viewBox="0 0 24 24" aria-hidden="true">' + paths + '</svg></span>';
  }

  function esc(s) { var d = document.createElement('div'); d.textContent = s; return d.innerHTML; }

  // "Key : Value" -> [key, value]  (only when the key is short and label-like)
  function kv(s) {
    var m = s.match(/^([^:]{2,42}?)\s*[:：]\s*(.+)$/);
    if (!m) return null;
    if (/\d{2,}$/.test(m[1])) return null;
    return [m[1].trim(), m[2].trim()];
  }

  /* "1 Cocoa Chocolate" -> "Cocoa Chocolate", or null when the line is not a
     sub-heading. Kept deliberately strict: a real heading is short, carries no
     sentence punctuation, and still has words left once the marker is gone. */
  function subName(t) {
    if (!SUB_RE.test(t)) return null;
    var rest = t.replace(SUB_RE, '').replace(/\s*[:：]\s*$/, '').trim();
    if (!rest) return null;
    if (rest.length > 60) return null;
    if (rest.split(/\s+/).length > 8) return null;
    if (/[.!?]$/.test(rest)) return null;
    return rest;
  }

  /* Give one figure its final box before the file arrives.
     Must run with the image already in the document - the theme's max-height
     is read from the live computed style, not assumed. */
  function reserve(im) {
    var w = parseFloat(im.getAttribute('width'));
    var h = parseFloat(im.getAttribute('height'));
    var guess = false;
    if (!(w > 0 && h > 0)) { w = 1; h = 1; guess = true; }

    var cap = 0;
    try {
      var mh = getComputedStyle(im).maxHeight;
      if (/px$/.test(mh)) cap = parseFloat(mh);
    } catch (e) {}

    var box = cap > 0 ? Math.round(cap * w / h) : Math.round(w);
    im.style.setProperty('aspect-ratio', w + ' / ' + h);
    im.style.setProperty('height', 'auto', 'important');
    im.style.setProperty('width', 'min(100%, ' + box + 'px)', 'important');

    /* No attributes to work from: the square above is a placeholder. Swap in
       the real numbers the moment the file reports them and re-measure. */
    if (guess) {
      var fix = function () {
        if (!im.naturalWidth || !im.naturalHeight) return;
        im.setAttribute('width', im.naturalWidth);
        im.setAttribute('height', im.naturalHeight);
        reserve(im);
      };
      if (im.complete) fix();
      else im.addEventListener('load', fix, { once: true });
    }
  }

  function reserveAll(host) {
    Array.prototype.forEach.call(host.querySelectorAll('.wp-ar__fig img'), function (im) {
      try { reserve(im); } catch (e) {}
    });
  }

  /* Flatten the description into an ordered list of lines and images,
     whatever wrapper markup Shopify's editor happened to leave behind. */
  function tokenise(root) {
    var BLOCK = /^(P|DIV|LI|UL|OL|H1|H2|H3|H4|H5|H6|SECTION|ARTICLE|TABLE|TR|BLOCKQUOTE|FIGURE)$/;
    var out = [];
    var buf = '';
    var seenImg = {};

    function flushLine() {
      var t = buf.replace(/ /g, ' ').replace(/\s+/g, ' ').trim();
      buf = '';
      if (!t) return;
      if (!/[A-Za-z0-9฀-๿]/.test(t)) return;
      out.push({ k: 'line', t: t });
    }

    function walk(node) {
      for (var i = 0; i < node.childNodes.length; i++) {
        var n = node.childNodes[i];
        if (n.nodeType === 3) { buf += n.nodeValue; continue; }
        if (n.nodeType !== 1) continue;
        var tag = n.tagName;
        if (tag === 'BR') { flushLine(); continue; }
        if (tag === 'IMG') {
          flushLine();
          var src = (n.getAttribute('src') || '').split('?')[0];
          if (src && seenImg[src]) continue;
          if (src) seenImg[src] = 1;
          out.push({ k: 'img', el: n });
          continue;
        }
        if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'IFRAME') continue;
        if (BLOCK.test(tag)) { flushLine(); walk(n); flushLine(); continue; }
        walk(n);                                  // strong, b, em, span, a ...
      }
    }
    walk(root);
    flushLine();
    return out;
  }

  /* Used by sections only. Inside a flavour card the same lines render as
     label/value rows instead, which stay aligned card to card. */
  function renderItems(items, host) {
    if (!items.length) return;
    var pairs = items.map(kv);
    var nPair = pairs.filter(Boolean).length;
    if (nPair >= 2 && nPair >= Math.ceil(items.length * 0.6)) {
      var tb = document.createElement('table');
      tb.className = 'wp-ar__spec';
      var tbody = document.createElement('tbody');
      items.forEach(function (it, i) {
        var tr = document.createElement('tr');
        if (pairs[i]) tr.innerHTML = '<th>' + esc(pairs[i][0]) + '</th><td>' + esc(pairs[i][1]) + '</td>';
        else tr.innerHTML = '<td colspan="2">' + esc(it) + '</td>';
        tbody.appendChild(tr);
      });
      tb.appendChild(tbody);
      host.appendChild(tb);
    } else {
      var longest = Math.max.apply(null, items.map(function (i) { return i.length; }));
      var ul = document.createElement('ul');
      ul.className = (items.length >= 4 && longest <= 58) ? 'wp-ar__feat' : 'wp-ar__dots';
      items.forEach(function (it) {
        var li = document.createElement('li');
        li.textContent = it;
        ul.appendChild(li);
      });
      host.appendChild(ul);
    }
  }

  var cssDone = false;
  function ensureGroupCss() {
    if (cssDone) return;
    cssDone = true;
    var s = document.createElement('style');
    s.id = 'wp-ar-fg-css';
    s.textContent =
      /* auto-fit, not a media query: this grid sits in a column whose width the
         viewport does not describe, so the column has to decide the count.
         The 300px floor is measured, not picked - at 248 the grid fitted three
         columns of 278px and the Thai FDA number, the widest value on the card,
         wrapped onto a second line on ten of the rows. 300 holds it to two
         columns wherever there is room for them, and one column below ~614px. */
      '.wb-fg{display:grid;gap:14px;margin-top:12px;' +
        'grid-template-columns:repeat(auto-fit,minmax(300px,1fr))}' +
      '.wb-fgi{padding:14px 16px;border-radius:16px;background:#FAFCFB;' +
        'border:1px solid #E8F0EB;transition:border-color .15s,box-shadow .15s,background .15s}' +
      '.wb-fgi.is-on{background:#fff;border-color:var(--wp-em2,#048C46);' +
        'box-shadow:inset 0 0 0 1px var(--wp-em2,#048C46)}' +
      '.wb-fgh{display:flex;align-items:center;gap:9px;margin:0 0 9px;' +
        'font-size:15px;font-weight:800;color:#0B3A27;line-height:1.5}' +
      '.wb-fgn{flex:0 0 24px;width:24px;height:24px;border-radius:8px;' +
        'background:#E3F2E8;color:var(--wp-em2,#048C46);font-size:12.5px;font-weight:800;' +
        'display:flex;align-items:center;justify-content:center}' +
      '.wb-fgi.is-on .wb-fgn{background:var(--wp-em2,#048C46);color:#fff}' +
      '.wb-fgt{min-width:0}' +
      '.wb-fgs{display:none;margin-left:auto;flex:none;font-size:10px;font-weight:700;' +
        'letter-spacing:.04em;text-transform:uppercase;background:var(--wp-em2,#048C46);' +
        'color:#fff;border-radius:999px;padding:3px 8px}' +
      '.wb-fgi.is-on .wb-fgs{display:inline-block}' +
      '.wb-fgl{margin:0;padding:0;list-style:none;display:grid;gap:5px}' +
      '.wb-fgl li{display:flex;align-items:baseline;gap:10px;position:relative;' +
        'padding-left:15px;font-size:13.5px;line-height:1.7;color:#4A6656}' +
      '.wb-fgl li::before{content:"";position:absolute;left:3px;top:.72em;width:4px;height:4px;' +
        'border-radius:50%;background:#9CC7AF}' +
      '.wb-fgl li>span{flex:1 1 auto;min-width:0}' +
      '.wb-fgl li>b{flex:none;font-weight:700;color:#0B3A27}' +
      '.wb-fgi>p{margin:0 0 8px;font-size:13.5px;line-height:1.7;color:#4A6656}' +
      '@media (max-width:560px){.wb-fgi{padding:12px 14px}}';
    (document.head || document.documentElement).appendChild(s);
  }

  function norm(s) { return (s || '').toLowerCase().replace(/[^a-z0-9฀-๿]+/g, ''); }

  /* Mark the card for the flavour currently chosen in the buy box. Every card
     stays open either way - this only answers "which one is mine". */
  function syncVariant() {
    var gs = document.querySelectorAll('.wb-fgi[data-ar-g]');
    if (!gs.length) return;
    var sel = document.getElementById('wpVariant');
    var label = '';
    if (sel && sel.selectedIndex >= 0 && sel.options[sel.selectedIndex]) {
      label = (sel.options[sel.selectedIndex].textContent || '').split('·')[0];
    }
    var key = norm(label);
    var hit = null;
    if (key) {
      Array.prototype.forEach.call(gs, function (g) {
        if (hit) return;
        var n = norm(g.getAttribute('data-ar-g'));
        if (n && (n === key || n.indexOf(key) >= 0 || key.indexOf(n) >= 0)) hit = g;
      });
    }
    Array.prototype.forEach.call(gs, function (g) {
      if (g === hit) g.classList.add('is-on');
      else g.classList.remove('is-on');
    });
  }

  function renderCards(groups, sec) {
    ensureGroupCss();
    var wrap = document.createElement('div');
    wrap.className = 'wb-fg';
    groups.forEach(function (g, gi) {
      var card = document.createElement('div');
      card.className = 'wb-fgi';
      card.setAttribute('data-ar-g', g.name);

      var h = document.createElement('h4');
      h.className = 'wb-fgh';
      var num = document.createElement('span');
      num.className = 'wb-fgn';
      num.textContent = gi + 1;
      h.appendChild(num);
      var nm = document.createElement('span');
      nm.className = 'wb-fgt';
      nm.textContent = g.name;
      h.appendChild(nm);
      var tag = document.createElement('span');
      tag.className = 'wb-fgs';
      tag.textContent = 'Selected';
      h.appendChild(tag);
      card.appendChild(h);

      g.paras.forEach(function (p) {
        var e = document.createElement('p');
        e.textContent = p;
        card.appendChild(e);
      });

      if (g.items.length) {
        var ul = document.createElement('ul');
        ul.className = 'wb-fgl';
        g.items.forEach(function (it) {
          var pair = kv(it);
          var li = document.createElement('li');
          if (pair) {
            var k = document.createElement('span');
            k.textContent = pair[0];
            var v = document.createElement('b');
            v.textContent = pair[1];
            li.appendChild(k);
            li.appendChild(v);
          } else {
            var only = document.createElement('span');
            only.textContent = it;
            li.appendChild(only);
          }
          ul.appendChild(li);
        });
        card.appendChild(ul);
      }
      wrap.appendChild(card);
    });
    sec.appendChild(wrap);
  }

  function build(root) {
    var raw = tokenise(root);
    if (!raw.length) return null;

    /* classify each line */
    var toks = [];
    raw.forEach(function (x) {
      if (x.k === 'img') { toks.push(x); return; }
      var t = x.t;
      if (/^[✅✔☑✓■●✦⭐]/.test(t)) {
        var body = t.replace(/^[✅✔☑✓■●✦⭐️\s]+/, '').trim();
        if (!body) return;
        var words = body.split(/\s+/).length;
        var shouty = /^[^a-z]{6,}$/.test(body);          // ALL-CAPS product line
        var isHeading = !/[.!?]$/.test(body) &&
          ((body.length <= 78 && words <= 14) || (shouty && body.length <= 120));
        toks.push({ k: isHeading ? 'h' : 'check', t: body.replace(/\s*[:：]\s*$/, '') });
        return;
      }
      if (/^[-–—•·▪○]\s*/.test(t)) {
        var it = t.replace(/^[-–—•·▪○]\s*/, '').trim();
        if (it) toks.push({ k: 'li', t: it });
        return;
      }
      var sub = subName(t);
      if (sub) { toks.push({ k: 'sub', t: sub }); return; }
      toks.push({ k: 'p', t: t });
    });
    if (!toks.some(function (x) { return x.k === 'h'; })) return null;

    var out = document.createElement('div');
    out.className = 'wp-ar';
    var figs = [];
    var secs = [];
    var cur = null;
    var leadDone = false;
    function flush() { if (cur) { secs.push(cur); cur = null; } }
    function blank(s) { return !s.title && !s.items.length && !s.checks.length && !s.paras.length && !s.g.length; }

    toks.forEach(function (x) {
      if (x.k === 'img') { figs.push(x.el); return; }
      if (x.k === 'h') {
        var empty = !cur || blank(cur);
        if (!leadDone && !secs.length && empty) { cur = null;
          var p = document.createElement('p');
          p.className = 'wp-ar__lead';
          p.textContent = x.t;
          out.appendChild(p);
          leadDone = true;
          return;
        }
        flush();
        cur = { title: x.t, items: [], checks: [], paras: [], g: [] };
        return;
      }
      if (!cur) cur = { title: '', items: [], checks: [], paras: [], g: [] };
      if (x.k === 'sub') { cur.g.push({ name: x.t, items: [], paras: [] }); return; }
      var grp = cur.g.length ? cur.g[cur.g.length - 1] : null;
      if (x.k === 'li') { (grp ? grp.items : cur.items).push(x.t); }
      else if (x.k === 'check') cur.checks.push(x.t);
      else { (grp ? grp.paras : cur.paras).push(x.t); }
    });
    flush();
    secs = secs.filter(function (s) { return s.title || s.items.length || s.checks.length || s.paras.length || s.g.length; });
    if (!secs.length) return null;

    /* Only keep the grouping when it really is one. Otherwise fold every group
       back into the flat section, which reproduces the previous output exactly. */
    secs.forEach(function (s) {
      if (!s.g.length) return;
      var solid = 0;
      s.g.forEach(function (g) { if (g.items.length >= 2) solid++; });
      if (s.g.length >= 2 && solid >= Math.ceil(s.g.length / 2)) return;
      s.g.forEach(function (g) {
        s.paras.push(g.name);
        g.paras.forEach(function (p) { s.paras.push(p); });
        g.items.forEach(function (i) { s.items.push(i); });
      });
      s.g = [];
    });

    if (figs.length) {
      var f = document.createElement('figure');
      f.className = 'wp-ar__fig';
      f.appendChild(figs.shift().cloneNode(true));
      out.appendChild(f);
    }
    var every = figs.length ? Math.max(2, Math.ceil(secs.length / (figs.length + 1))) : 0;

    secs.forEach(function (s, idx) {
      var meta = pick(s.title || '');
      var sec = document.createElement('section');
      sec.className = 'wp-ar__s wp-ar__s--' + meta.mod;
      if (s.title) {
        var h = document.createElement('h3');
        h.className = 'wp-ar__h';
        h.innerHTML = svg(meta.icon) + '<span>' + esc(s.title) + '</span>';
        sec.appendChild(h);
      }
      s.paras.forEach(function (p) {
        var el = document.createElement('p');
        el.textContent = p;
        sec.appendChild(el);
      });
      renderItems(s.items, sec);
      if (s.checks.length) {
        var short = s.checks.every(function (c) { return c.length <= 46; });
        var ulc = document.createElement('ul');
        ulc.className = short ? 'wp-ar__badges' : 'wp-ar__feat';
        s.checks.forEach(function (c) {
          var li = document.createElement('li');
          li.textContent = c;
          ulc.appendChild(li);
        });
        sec.appendChild(ulc);
      }
      if (s.g.length) renderCards(s.g, sec);
      out.appendChild(sec);
      if (every && figs.length && (idx + 1) % every === 0) {
        var fg = document.createElement('figure');
        fg.className = 'wp-ar__fig';
        fg.appendChild(figs.shift().cloneNode(true));
        out.appendChild(fg);
      }
    });
    while (figs.length) {
      var fl = document.createElement('figure');
      fl.className = 'wp-ar__fig';
      fl.appendChild(figs.shift().cloneNode(true));
      out.appendChild(fl);
    }
    /* width and height stay on purpose - reserve() needs them, and they are
       the only record of the file's real shape before it loads. */
    Array.prototype.forEach.call(out.querySelectorAll('img'), function (im) {
      im.setAttribute('loading', 'lazy');
      im.setAttribute('decoding', 'async');
      if (!im.getAttribute('alt')) im.setAttribute('alt', (document.title || '').split('|')[0].trim());
    });
    return out;
  }

  function run() {
    var hosts = document.querySelectorAll('.wp-pdp__rte:not([data-arfmt])');
    Array.prototype.forEach.call(hosts, function (host) {
      host.setAttribute('data-arfmt', '1');
      try {
        var built = build(host);
        if (built) {
          host.innerHTML = '';
          host.appendChild(built);
          reserveAll(host);          // after insertion - reserve() reads live styles
        }
      } catch (e) { /* leave the original description untouched */ }
    });
    try { syncVariant(); } catch (e) {}
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  document.addEventListener('shopify:section:load', run);
  document.addEventListener('change', function (e) {
    if (e && e.target && e.target.id === 'wpVariant') { try { syncVariant(); } catch (x) {} }
  });
})();